/** Shared loading / empty / error shells so every tab behaves the same. */
import { Loader2 } from "lucide-react";

export function TabLoading({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label}
    </div>
  );
}

export function TabEmpty({ message }: { message: string }) {
  return (
    <div className="py-16 text-center text-sm text-gray-500">{message}</div>
  );
}

export function TabError({ error }: { error: Error }) {
  return (
    <div className="m-4 rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
      {error.message}
    </div>
  );
}

/** Card wrapper matching ExecBridge's surface treatment.
 *  Nested inside the Data Hub artifact shell — no outer border/shadow. */
export function TabCard({
  title,
  subtitle,
  actions,
  children,
  noPadding,
  scrollBody,
  bodyMaxHeight = "max-h-[min(55vh,560px)]",
}: {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  /** When true, body has no padding (e.g. full-bleed tables). */
  noPadding?: boolean;
  /** Scroll the body instead of growing the whole Metrics Upload page. */
  scrollBody?: boolean;
  /** Tailwind max-height class used when `scrollBody` is true. */
  bodyMaxHeight?: string;
}) {
  return (
    <div className="flex min-h-0 flex-col">
      {(title || actions) && (
        <div className="relative z-20 flex shrink-0 flex-col gap-2.5 overflow-visible border-b border-gray-200 px-4 py-3 sm:flex-row sm:flex-nowrap sm:items-center sm:gap-3">
          <div className="min-w-0 shrink-0">
            {title && (
              <p className="m-0 text-base font-semibold text-gray-900">
                {title}
              </p>
            )}
            {subtitle && (
              <p className="m-0 mt-0.5 text-xs text-gray-500">{subtitle}</p>
            )}
          </div>
          {actions && (
            <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
              {actions}
            </div>
          )}
        </div>
      )}
      <div
        className={`relative z-0 ${noPadding ? "" : "p-4"} ${
          scrollBody
            ? `${bodyMaxHeight} overflow-y-auto overscroll-contain`
            : ""
        }`}
      >
        {children}
      </div>
    </div>
  );
}
